import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ProductCatalogCard } from "@/components/products/ProductCatalogCard";
import { ProductDetailsDialog } from "@/components/products/ProductDetailsDialog";
import { useProductEvents } from "@/hooks/use-product-events";
import { useToast } from "@/hooks/use-toast";
import { buyAgainCadenceDisplay } from "@shared/buy-again-cadence";
import type { Product } from "@/types/product";

export interface BuyAgainItem {
  product: Product;
  /** Mean days between purchases (from org purchase stats). */
  avgIntervalDays?: number;
  /** Composite buy-again score (0–1, higher = more due) */
  score?: number;
  /** Days elapsed since the product was last purchased */
  gapDays?: number;
  /** Z-score: how many std deviations past the average reorder interval */
  z?: number;
  /** Normalised gap-closeness signal (0–1) */
  gapCloseness?: number;
  /** Normalised purchase-volume signal (0–1) */
  volume?: number;
  /** Normalised purchase-stability signal (0–1) */
  stability?: number;
}

interface BuyAgainSectionProps {
  items: BuyAgainItem[];
  onProductSelect: (product: Product, quantity: number) => void;
  selectedProducts: Array<{ productId: string; quantity: number; unitPrice?: number }>;
}

function DismissMenu({
  anchorRef,
  onSelect,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onSelect: (key: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const dismissOptions = [
    { key: "too_soon",      label: t("components.orders.buyAgainSection.tooSoonLabel"),      sub: t("components.orders.buyAgainSection.tooSoonSub")      },
    { key: "have_enough",   label: t("components.orders.buyAgainSection.haveEnoughLabel"),   sub: t("components.orders.buyAgainSection.haveEnoughSub")   },
    { key: "wrong_jobs",    label: t("components.orders.buyAgainSection.wrongJobsLabel"),    sub: t("components.orders.buyAgainSection.wrongJobsSub")    },
    { key: "wrong_product", label: t("components.orders.buyAgainSection.wrongProductLabel"), sub: t("components.orders.buyAgainSection.wrongProductSub") },
    { key: "hide_sku",      label: t("components.orders.buyAgainSection.hideSkuLabel"),      sub: t("components.orders.buyAgainSection.hideSkuSub")      },
  ];

  useEffect(() => {
    if (anchorRef.current) {
      const r = anchorRef.current.getBoundingClientRect();
      const menuWidth = 224;
      const left = Math.min(r.right - menuWidth, window.innerWidth - menuWidth - 8);
      setPos({ top: r.bottom + 4, left: Math.max(8, left) });
    }
  }, [anchorRef]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose, anchorRef]);

  return createPortal(
    <div
      ref={menuRef}
      style={{
        position: "fixed",
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        width: 224,
        zIndex: 9999,
        visibility: pos ? "visible" : "hidden",
      }}
      className="bg-white border border-border rounded-lg shadow-lg overflow-hidden"
    >
      <p className="px-3 pt-3 pb-3 text-[11px] text-muted-foreground">{t("components.orders.buyAgainSection.helpsTuneWhatWe")}</p>
      <div className="px-3 pb-3 flex flex-col gap-2">
        {dismissOptions.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => { onSelect(opt.key); onClose(); }}
            className="w-full text-left px-3 py-2 rounded-md border border-border bg-muted/40 hover:bg-muted active:bg-muted transition-colors"
          >
            <p className="text-sm font-medium text-foreground">{opt.label}</p>
            <p className="text-[11px] text-muted-foreground">{opt.sub}</p>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
}

function BuyAgainCard({
  item,
  quantity,
  onQuantityChange,
  onFeedback,
  onCardClick,
}: {
  item: BuyAgainItem;
  quantity: number;
  onQuantityChange: (qty: number) => void;
  onFeedback: (reason: string) => void;
  onCardClick: () => void;
}) {
  const { t } = useTranslation();
  const { product, avgIntervalDays, z } = item;
  const [menuOpen, setMenuOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const noteText =
    avgIntervalDays != null && avgIntervalDays > 0
      ? (() => {
          const { count, unit } = buyAgainCadenceDisplay(avgIntervalDays);
          return unit === "weeks"
            ? t("components.orders.buyAgainSection.orderedEveryWeeks", { count })
            : t("components.orders.buyAgainSection.orderedEveryDays", { count });
        })()
      : null;

  const urgencyBadge =
    z === undefined
      ? { label: t("components.orders.buyAgainSection.urgencyReorder"),    className: "bg-blue-100   text-blue-600"   }
      : z < 0
        ? { label: t("components.orders.buyAgainSection.urgencyRunningLow"), className: "bg-orange-100 text-orange-600" }
        : { label: t("components.orders.buyAgainSection.urgencyRestockNow"), className: "bg-red-100    text-red-600"    };

  return (
    <div className="flex-shrink-0 w-[65vw] sm:w-auto flex flex-col gap-1 self-stretch">
      {/* Badge + 3-dot in their own row — never overlaps card content */}
      <div className="flex items-center justify-between px-0.5">
        <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-sm tracking-wide ${urgencyBadge.className}`}>
          {urgencyBadge.label}
        </span>
        <button
          ref={btnRef}
          type="button"
          onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
          className="p-1 rounded hover:bg-black/5 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t("components.orders.buyAgainSection.dismissOptions")}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
        {menuOpen && (
          <DismissMenu
            anchorRef={btnRef}
            onSelect={onFeedback}
            onClose={() => setMenuOpen(false)}
          />
        )}
      </div>

      <ProductCatalogCard
        product={product}
        quantity={quantity}
        onQuantityChange={onQuantityChange}
        showCachedPricing
        onCardClick={onCardClick}
        className="flex-1"
      />

      <p className="text-[11px] text-muted-foreground px-1 min-h-[2rem] line-clamp-2">
        {noteText}
      </p>
    </div>
  );
}

function buildScoringContext(item: BuyAgainItem) {
  return {
    price: item.product.currentPrice ?? undefined,
    score: item.score,
    gapDays: item.gapDays,
    z: item.z,
    gapCloseness: item.gapCloseness,
    volume: item.volume,
    stability: item.stability,
  };
}

export function BuyAgainSection({
  items,
  onProductSelect,
  selectedProducts,
}: BuyAgainSectionProps) {
  const { t } = useTranslation();
  const { capture } = useProductEvents({ component: "BuyAgainSection" });
  const { toast } = useToast();
  const [selectedProductForDialog, setSelectedProductForDialog] = useState<Product | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleCardClick = useCallback((product: Product) => {
    setSelectedProductForDialog(product);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) setSelectedProductForDialog(null);
  }, []);

  // Display most overdue first; items without a z-score go last.
  const sortedItems = [...items].sort(
    (a, b) => (b.z ?? -Infinity) - (a.z ?? -Infinity),
  );

  if (sortedItems.length === 0) return null;

  const getQuantity = (productId: string) =>
    selectedProducts.find((p) => p.productId === productId)?.quantity ?? 0;

  const dialogItemIndex = selectedProductForDialog
    ? sortedItems.findIndex(
        (i) => i.product.uid === selectedProductForDialog.uid,
      )
    : -1;
  const dialogItem = dialogItemIndex >= 0 ? sortedItems[dialogItemIndex] : null;

  const handleQuantityChange = (item: BuyAgainItem, rank: number, qty: number) => {
    const currentQty = getQuantity(item.product.uid);
    if (qty > 0 && currentQty === 0) {
      capture({
        event: "add_buy_again_product" as const,
        context: {
          productId: item.product.uid,
          rank,
          ...buildScoringContext(item),
        },
      });
    }
    onProductSelect(item.product, qty);
  };

  const handleFeedback = (item: BuyAgainItem, rank: number, reason: string) => {
    capture({
      event: "buy_again_user_feedback" as const,
      context: {
        productId: item.product.uid,
        rank,
        reason,
        ...buildScoringContext(item),
      },
    });
    toast({
      title: t("components.orders.buyAgainSection.feedbackTitle"),
      description: t("components.orders.buyAgainSection.feedbackDescription"),
    });
  };

  return (
    <>
      <div className="rounded-lg border-2 border-border bg-card px-4 py-3 shadow-sm space-y-2">
        <div>
          <h2 className="text-xl font-bold">{t("components.orders.buyAgainSection.timeToReorder")}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t("components.orders.buyAgainSection.basedOnHowOften")}
          </p>
        </div>

        <div className="flex gap-4 overflow-x-auto py-2 px-1 sm:px-0 sm:overflow-x-visible sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 scrollbar-thin">
          {sortedItems.map((item, index) => (
            <BuyAgainCard
              key={item.product.uid}
              item={item}
              quantity={getQuantity(item.product.uid)}
              onQuantityChange={(qty) => handleQuantityChange(item, index + 1, qty)}
              onFeedback={(reason) => handleFeedback(item, index + 1, reason)}
              onCardClick={() => handleCardClick(item.product)}
            />
          ))}
        </div>
      </div>
      {selectedProductForDialog && (
        <ProductDetailsDialog
          product={selectedProductForDialog}
          open={dialogOpen}
          onOpenChange={handleDialogClose}
          quantity={getQuantity(selectedProductForDialog.uid)}
          onQuantityChange={
            dialogItem
              ? (qty) => handleQuantityChange(dialogItem, dialogItemIndex + 1, qty)
              : undefined
          }
          showCachedPricing
        />
      )}
    </>
  );
}
